// src/pages/MisTalleresInscritos.tsx
import React, { useEffect, useRef, useState } from 'react';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../../firebaseConfig';
import { QRCodeCanvas } from 'qrcode.react';
import { calcularEstadoTaller, obtenerBadgeEstado } from '../../utils/tallerEstadoHelper';

import UserNav from '../../components/navbar/user-nav';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import PageTitle from '../../components/page-title';
import { LoadingMessage, EmptyState } from '../../components/loading-states';

interface Taller {
  taller_id: string;
  nombre: string;
  fecha?: Date;
  fechaInicio?: Date;
  fechaTermino?: Date;
  horaInicio?: string;
  horaTermino?: string;
  lugar?: string;
  ciudad?: string;
  organizadorPrincipal?: string;
  expositor?: string;
  creditos?: number;
  tipoCosto?: string;
  precio?: number;
  moneda?: string;
  tipoEvento?: string;
  estado?: 'proximo' | 'en_curso' | 'finalizado' | 'cancelado';
}

interface Inscripcion {
  taller_doc_id: string;  // ✅ Usar doc.id de Firestore
  codigo_qr: string;
  estatus_pago: string;
  completado: boolean;
  asistencia?: boolean;
  tipo_perfil: string;
  creditos?: number;
  fechaInscripcion?: any;
}

export default function MisTalleresInscritos() {
  const [perfilId, setPerfilId] = useState<string | null>(null);
  const [inscripciones, setInscripciones] = useState<Inscripcion[]>([]);
  const [talleres, setTalleres] = useState<{ [key: string]: Taller }>({});
  const qrRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Estados para filtros
  const [filtroNombre, setFiltroNombre] = useState('');
  const [filtroEstado, setFiltroEstado] = useState<'todos' | 'proximo' | 'en_curso' | 'finalizado' | 'cancelado'>('todos');
  const [filtroPago, setFiltroPago] = useState<'todos' | 'completado' | 'pendiente'>('todos');
  const [filtroAsistencia, setFiltroAsistencia] = useState<'todos' | 'registrada' | 'sin_registrar'>('todos');

  // Estado para QR Modal
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [qrModalData, setQrModalData] = useState<{ codigo: string; nombre: string; index: number } | null>(null);

  useEffect(() => {
    const fetchPerfil = async () => {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) return;

      const snapAsesor = await getDocs(query(collection(db, 'asesor'), where('correo', '==', user.email)));
      if (!snapAsesor.empty) {
        setPerfilId(snapAsesor.docs[0].id);
        return;
      }

      const snapAsociado = await getDocs(query(collection(db, 'asociado'), where('correo', '==', user.email)));
      if (!snapAsociado.empty) {
        setPerfilId(snapAsociado.docs[0].id);
      }
    };

    fetchPerfil();
  }, []);

  useEffect(() => {
    if (!perfilId) return;

    const fetchData = async () => {
      // 1. Obtener inscripciones del perfil
      const insSnap = await getDocs(query(
        collection(db, 'inscripcion'),
        where('perfil_id', '==', perfilId)
      ));
      const insData: Inscripcion[] = insSnap.docs.map(doc => doc.data() as Inscripcion);
      setInscripciones(insData);

      // 2. Cargar talleres usando doc.id directo (más eficiente)
      const allTalleres: { [key: string]: Taller } = {};

      for (const ins of insData) {
        if (!ins.taller_doc_id) continue;

        try {
          const tallerDocRef = doc(db, 'taller', ins.taller_doc_id);
          const tallerSnap = await getDoc(tallerDocRef);

          if (tallerSnap.exists()) {
            const d = tallerSnap.data();
            const fechaTaller = d.fecha?.toDate ? d.fecha.toDate() : d.fechaInicio?.toDate ? d.fechaInicio.toDate() : null;

            // Calcular estado automáticamente
            let estado: 'proximo' | 'en_curso' | 'finalizado' | 'cancelado' | undefined = d.estado;
            if (fechaTaller && d.horaInicio && d.horaTermino) {
              estado = calcularEstadoTaller(fechaTaller, d.horaInicio, d.horaTermino, d.estado);
            }

            allTalleres[ins.taller_doc_id] = {
              taller_id: ins.taller_doc_id,
              nombre: d.nombre,
              fecha: d.fecha?.toDate ? d.fecha.toDate() : d.fecha,
              fechaInicio: d.fechaInicio?.toDate ? d.fechaInicio.toDate() : d.fechaInicio,
              fechaTermino: d.fechaTermino?.toDate ? d.fechaTermino.toDate() : d.fechaTermino,
              horaInicio: d.horaInicio,
              horaTermino: d.horaTermino,
              lugar: d.lugar,
              ciudad: d.ciudad,
              organizadorPrincipal: d.organizadorPrincipal,
              expositor: d.expositor,
              creditos: d.creditos,
              tipoCosto: d.tipoCosto,
              precio: d.precio,
              moneda: d.moneda,
              tipoEvento: d.tipoEvento,
              estado: estado
            };
          }
        } catch (error) {
          console.error(`Error cargando taller ${ins.taller_doc_id}:`, error);
        }
      }

      setTalleres(allTalleres);
    };

    fetchData();
  }, [perfilId]);

  const handleDownloadQR = (index: number, nombreTaller: string) => {
    const container = qrRefs.current[index];
    if (!container) return;

    const canvas = container.querySelector('canvas');
    if (!canvas) return;

    const pngUrl = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.href = pngUrl;
    link.download = `QR-${nombreTaller}.png`;
    link.click();
  };

  const handleVerQR = (codigoQR: string, nombreTaller: string, index: number) => {
    setQrModalData({ codigo: codigoQR, nombre: nombreTaller, index });
    setQrModalVisible(true);
  };

  const limpiarFiltros = () => {
    setFiltroNombre('');
    setFiltroEstado('todos');
    setFiltroPago('todos');
    setFiltroAsistencia('todos');
  };

  const hoy = new Date();

  // Inscritos con taller (antes de filtros)
  let inscritosConTaller = inscripciones
    .filter(ins => talleres[ins.taller_doc_id])
    .map((ins, i) => ({
      ...ins,
      taller: talleres[ins.taller_doc_id],
      index: i
    }));

  // Aplicar filtros
  if (filtroNombre) {
    inscritosConTaller = inscritosConTaller.filter(item =>
      item.taller.nombre.toLowerCase().includes(filtroNombre.toLowerCase())
    );
  }

  if (filtroEstado !== 'todos') {
    inscritosConTaller = inscritosConTaller.filter(item =>
      item.taller.estado === filtroEstado
    );
  }

  if (filtroPago !== 'todos') {
    inscritosConTaller = inscritosConTaller.filter(item =>
      item.estatus_pago === filtroPago
    );
  }

  if (filtroAsistencia === 'registrada') {
    inscritosConTaller = inscritosConTaller.filter(item => item.asistencia === true);
  } else if (filtroAsistencia === 'sin_registrar') {
    inscritosConTaller = inscritosConTaller.filter(item => !item.asistencia);
  }

  const proximos = inscritosConTaller
    .filter(item => {
      const fecha = item.taller.fecha || item.taller.fechaTermino || item.taller.fechaInicio;
      return fecha && fecha > hoy;
    })
    .sort((a, b) => {
      const fechaA = (a.taller.fecha || a.taller.fechaInicio)?.getTime() || 0;
      const fechaB = (b.taller.fecha || b.taller.fechaInicio)?.getTime() || 0;
      return fechaA - fechaB;
    });

  const finalizados = inscritosConTaller
    .filter(item => {
      const fecha = item.taller.fecha || item.taller.fechaTermino || item.taller.fechaInicio;
      return fecha && fecha <= hoy;
    })
    .sort((a, b) => {
      const fechaA = (a.taller.fecha || a.taller.fechaInicio)?.getTime() || 0;
      const fechaB = (b.taller.fecha || b.taller.fechaInicio)?.getTime() || 0;
      return fechaB - fechaA;
    });

  const renderTaller = (item: any, showQR: boolean) => {
    const badgeEstado = obtenerBadgeEstado(item.taller.estado);

    return (
      <div className="col-lg-6 mb-4" key={item.index}>
        <div className="card p-4 shadow-sm h-100">
          <div className="d-flex justify-content-between align-items-start mb-3">
            <h5 className="mb-0">{item.taller.nombre}</h5>
            <span className={`badge ${badgeEstado.className} ms-2`}>
              {badgeEstado.texto}
            </span>
          </div>

          <div className="mb-3">
            <p className="mb-2">
              <i className="fa-solid fa-calendar me-2 text-primary"></i>
              <strong>Fecha:</strong> {' '}
              {(item.taller.fecha || item.taller.fechaInicio)?.toLocaleDateString('es-MX')}
            </p>
            <p className="mb-2">
              <i className="fa-solid fa-clock me-2 text-primary"></i>
              <strong>Horario:</strong> {' '}
              {item.taller.horaInicio && item.taller.horaTermino
                ? `${item.taller.horaInicio} - ${item.taller.horaTermino}`
                : 'Por definir'}
            </p>
            {item.taller.lugar && (
              <p className="mb-2">
                <i className="fa-solid fa-location-dot me-2 text-primary"></i>
                <strong>Lugar:</strong> {item.taller.lugar}
                {item.taller.ciudad && `, ${item.taller.ciudad}`}
              </p>
            )}
            <p className="mb-2">
              <i className="fa-solid fa-user me-2 text-primary"></i>
              <strong>Organizador:</strong> {' '}
              {item.taller.organizadorPrincipal || item.taller.expositor || 'AMPI'}
            </p>
            {item.creditos !== undefined && (
              <p className="mb-2">
                <i className="fa-solid fa-award me-2 text-primary"></i>
                <strong>Créditos:</strong> {item.creditos}
              </p>
            )}
          </div>

          <div className="mb-3">
            <span className="me-2">
              <strong>Pago:</strong>{' '}
              <span className={`badge ${
                item.estatus_pago === 'completado' ? 'bg-success' : 'bg-warning text-dark'
              }`}>
                {item.estatus_pago === 'completado' ? '✓ Completado' : '⏳ Pendiente'}
              </span>
            </span>
            <span>
              <strong>Asistencia:</strong>{' '}
              <span className={`badge ${
                item.asistencia ? 'bg-success' : 'bg-secondary'
              }`}>
                {item.asistencia ? '✓ Registrada' : '○ Sin registrar'}
              </span>
            </span>
          </div>

          {showQR && (
            <>
              <div className="d-none" ref={(el) => (qrRefs.current[item.index] = el)}>
                <QRCodeCanvas value={item.codigo_qr} size={200} />
              </div>
              <div className="d-flex gap-2 mt-auto">
                <button
                  onClick={() => handleVerQR(item.codigo_qr, item.taller.nombre, item.index)}
                  className="btn btn-sm btn-outline-primary flex-grow-1"
                >
                  <i className="fa-solid fa-eye me-1"></i> Ver QR
                </button>
                <button
                  onClick={() => handleDownloadQR(item.index, item.taller.nombre)}
                  className="btn btn-sm btn-outline-success flex-grow-1"
                >
                  <i className="fa-solid fa-download me-1"></i> Descargar QR
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <UserNav />
      <PageTitle
        title="Mis Talleres Inscritos"
        subtitle="Consulta tu QR para validar asistencia"
      />

      <section className="gray-simple">
        <div className="container">
          {/* Filtros */}
          <div className="card mb-4 p-3 shadow-sm">
            <h5 className="mb-3">
              <i className="fa-solid fa-filter me-2"></i>Filtros de Búsqueda
            </h5>
            <div className="row g-3">
              {/* Filtro por nombre */}
              <div className="col-md-6 col-lg-3">
                <label className="form-label small fw-bold">Nombre del Taller</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar por nombre..."
                  value={filtroNombre}
                  onChange={(e) => setFiltroNombre(e.target.value)}
                />
              </div>

              {/* Filtro por estado */}
              <div className="col-md-6 col-lg-3">
                <label className="form-label small fw-bold">Estado</label>
                <select
                  className="form-select"
                  value={filtroEstado}
                  onChange={(e) => setFiltroEstado(e.target.value as any)}
                >
                  <option value="todos">Todos</option>
                  <option value="proximo">📅 Próximo</option>
                  <option value="en_curso">▶️ En Curso</option>
                  <option value="finalizado">✅ Finalizado</option>
                  <option value="cancelado">❌ Cancelado</option>
                </select>
              </div>

              {/* Filtro por pago */}
              <div className="col-md-6 col-lg-3">
                <label className="form-label small fw-bold">Estatus de Pago</label>
                <select
                  className="form-select"
                  value={filtroPago}
                  onChange={(e) => setFiltroPago(e.target.value as any)}
                >
                  <option value="todos">Todos</option>
                  <option value="completado">✓ Completado</option>
                  <option value="pendiente">⏳ Pendiente</option>
                </select>
              </div>

              {/* Filtro por asistencia */}
              <div className="col-md-6 col-lg-3">
                <label className="form-label small fw-bold">Asistencia</label>
                <select
                  className="form-select"
                  value={filtroAsistencia}
                  onChange={(e) => setFiltroAsistencia(e.target.value as any)}
                >
                  <option value="todos">Todos</option>
                  <option value="registrada">✓ Registrada</option>
                  <option value="sin_registrar">○ Sin Registrar</option>
                </select>
              </div>
            </div>

            {/* Botón para limpiar filtros */}
            <div className="mt-3 text-end">
              <button className="btn btn-outline-secondary btn-sm" onClick={limpiarFiltros}>
                <i className="fa-solid fa-times me-1"></i>Limpiar Filtros
              </button>
              <span className="ms-3 text-muted small">
                Mostrando {inscritosConTaller.length} de {inscripciones.length} talleres
              </span>
            </div>
          </div>

          {inscripciones.length === 0 ? (
            <div className="alert alert-info">
              <i className="fa-solid fa-info-circle me-2"></i>
              No tienes talleres inscritos.
            </div>
          ) : inscritosConTaller.length === 0 ? (
            <div className="alert alert-warning">
              <i className="fa-solid fa-search me-2"></i>
              No se encontraron talleres con los filtros aplicados.
            </div>
          ) : (
            <>
              {proximos.length > 0 && (
                <>
                  <h4 className="mb-3">
                    <i className="fa-solid fa-calendar-days me-2 text-primary"></i>
                    Próximos Talleres ({proximos.length})
                  </h4>
                  <div className="row">{proximos.map(item => renderTaller(item, true))}</div>
                </>
              )}
              {finalizados.length > 0 && (
                <>
                  <h4 className="mt-5 mb-3">
                    <i className="fa-solid fa-calendar-check me-2 text-secondary"></i>
                    Talleres Finalizados ({finalizados.length})
                  </h4>
                  <div className="row">{finalizados.map(item => renderTaller(item, true))}</div>
                </>
              )}
            </>
          )}
        </div>
      </section>

      {/* QR Modal */}
      {qrModalVisible && qrModalData && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={() => setQrModalVisible(false)}
        >
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fa-solid fa-qrcode me-2"></i>
                  Código QR - {qrModalData.nombre}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setQrModalVisible(false)}
                ></button>
              </div>
              <div className="modal-body text-center">
                <QRCodeCanvas value={qrModalData.codigo} size={300} />
                <p className="text-muted mt-3 mb-0">
                  <i className="fa-solid fa-mobile-screen me-2"></i>
                  Presenta este código QR en el taller para registrar tu asistencia
                </p>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-success"
                  onClick={() => {
                    handleDownloadQR(qrModalData.index, qrModalData.nombre);
                  }}
                >
                  <i className="fa-solid fa-download me-2"></i>
                  Descargar QR
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => setQrModalVisible(false)}
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
